import React from 'react';
import { primitiveRenderers, primitiveValidators } from './MathVisualPrimitives';
import ShapePatternVisual, { supportsShapePattern } from './ShapePatternVisual';

const INK = '#172554';
const BLUE = '#2563eb';
const ORANGE = '#f97316';
const shapeNames = { '▲': 'üçgen', '■': 'kare', '⬟': 'beşgen', '?': 'soru işareti' };

function rationalValue(value) {
  if (typeof value !== 'string' || !/^-?\d+(?:\/-?\d+)?$/.test(value)) return null;
  const [numerator, denominator = '1'] = value.split('/').map(Number);
  return denominator === 0 ? null : numerator / denominator;
}

function NumberLineAxis({ lower, upper, labelSize = 28, muted = false }) {
  return <>
    <line x1="100" y1="145" x2="500" y2="145" stroke={muted ? '#cbd5e1' : INK} strokeWidth={muted ? 2 : 4} />
    {[100, 500].map((tick) => <line key={tick} x1={tick} x2={tick} y1="130" y2="160" stroke={INK} strokeWidth="3" />)}
    <text x="100" y="195" fontSize={labelSize}>{lower}</text>
    <text x="500" y="195" fontSize={labelSize}>{upper}</text>
  </>;
}

const renderers = {
  ...primitiveRenderers,
  analog_clock: ({ hour, minute }) => <>
    <circle cx="140" cy="140" r="126" fill="#f8fafc" stroke={BLUE} strokeWidth="4" />
    {Array.from({ length: 60 }, (_, index) => {
      const angle = index * Math.PI / 30;
      const inner = index % 5 === 0 ? 110 : 116;
      return <line key={index} x1={140 + inner * Math.sin(angle)} y1={140 - inner * Math.cos(angle)} x2={140 + 121 * Math.sin(angle)} y2={140 - 121 * Math.cos(angle)} stroke={INK} strokeWidth={index % 5 === 0 ? 3 : 1} />;
    })}
    {Array.from({ length: 12 }, (_, index) => {
      const number = index + 1;
      const angle = number * Math.PI / 6;
      return <text key={number} x={140 + 93 * Math.sin(angle)} y={140 - 93 * Math.cos(angle)} dominantBaseline="central" fontSize="23">{number}</text>;
    })}
    <line data-testid="clock-hour-hand" x1="140" y1="148" x2="140" y2="82" transform={`rotate(${(hour % 12) * 30 + minute / 2} 140 140)`} stroke={INK} strokeWidth="8" strokeLinecap="round" />
    <line data-testid="clock-minute-hand" x1="140" y1="150" x2="140" y2="57" transform={`rotate(${minute * 6} 140 140)`} stroke={BLUE} strokeWidth="5" strokeLinecap="round" />
    <circle cx="140" cy="140" r="6" fill={INK} />
  </>,
  base10_blocks: ({ number }) => {
    const [tens, ones] = [Math.floor(number / 10), number % 10];
    return <>
      <text x="300" y="25" fill="#1d4ed8" fontSize="20">Onluk</text>
      {Array.from({ length: tens }, (_, index) => {
        const x = 300 - (tens * 52) / 2 + index * 52 + 4;
        return <g key={`ten-${index}`}>
          <rect x={x} y="35" width="44" height="140" rx="5" fill="#93c5fd" stroke={BLUE} />
          {Array.from({ length: 9 }, (_, cell) => <line key={cell} x1={x} x2={x + 44} y1={49 + cell * 14} y2={49 + cell * 14} stroke="#64748b" />)}
        </g>;
      })}
      <text x="300" y="198" fill="#c2410c" fontSize="20">Birlik</text>
      {Array.from({ length: ones }, (_, index) => <rect key={`one-${index}`} x={300 - (ones * 52) / 2 + index * 52 + 4} y="210" width="44" height="44" rx="4" fill="#fdba74" stroke="#ea580c" />)}
    </>;
  },
  numberline_rounding: ({ number, unit = 10 }) => {
    const left = Math.floor(number / unit) * unit;
    const x = 100 + ((number - left) / unit) * 400;
    return <>
      <NumberLineAxis lower={left} upper={left + unit} />
      <circle cx={x} cy="145" r="9" fill="#ef4444" />
      <text x={x} y="112" fill="#b91c1c" fontSize="26">{number}</text>
    </>;
  },
  numberline_midpoint: ({ lower, upper }) => <>
    <NumberLineAxis lower={lower} upper={upper} labelSize={34} />
    <line x1="300" y1="130" x2="300" y2="160" stroke={INK} strokeWidth="3" />
    <circle data-testid="midpoint-marker" cx="300" cy="145" r="10" fill="#8b5cf6" />
    <text x="300" y="195" fontSize="36">?</text>
  </>,
  numberline_pattern: ({ values, direction }) => <>
    <NumberLineAxis lower={values[0] ?? '?'} upper={values[4] ?? '?'} labelSize={30} />
    {values.slice(1, 4).map((value, i) => <g key={i}>
      <line x1={200 + i * 100} x2={200 + i * 100} y1="135" y2="155" stroke={INK} strokeWidth="3" />
      <text x={200 + i * 100} y="195" fontSize="30">{value ?? '?'}</text>
    </g>)}
    <circle data-testid="pattern-missing-marker" cx={100 + values.indexOf(null) * 100} cy="145" r="9" fill="#8b5cf6" />
    <text x="300" y="50" fontSize="22">{direction === 'descending' ? 'Geriye doğru' : 'İleriye doğru'}</text>
    <g data-testid="pattern-direction" data-direction={direction}>
      <Arrow x1={direction === 'descending' ? 490 : 110} x2={direction === 'descending' ? 110 : 490} y1={85} y2={85} />
    </g>
  </>,
  numberline_interval: ({ lower, upper }) => <>
    <rect x="105" y="132" width="390" height="26" rx="10" fill="#ede9fe" />
    <NumberLineAxis lower={lower} upper={upper} labelSize={32} />
    {[100, 500].map((x) => <circle key={x} cx={x} cy="145" r="7" fill="white" stroke={BLUE} strokeWidth="3" />)}
    <text x="300" y="90" fontSize="24">Aradaki sayı?</text>
  </>,
  neighbor_tens: ({ number, lower, upper }) => <>
    {[{ value: lower, x: 55 }, { value: upper, x: 405 }].map(({ value, x }) => <g key={value}>
      <rect x={x} y="95" width="140" height="90" rx="15" fill="#eff6ff" stroke={BLUE} strokeWidth="2" />
      <text x={x + 70} y="150" fontSize="32">{value}</text>
    </g>)}
    <text x="300" y="150" fontSize="32">{number}</text>
  </>,
  minimal_number_position: ({ number, lower, upper }) => {
    const x = 100 + ((number - lower) / (upper - lower)) * 400;
    return <>
      <NumberLineAxis lower={lower} upper={upper} muted />
      <circle cx={x} cy="145" r="7" fill={BLUE} />
      <text x={x} y="112" fontSize="26">{number}</text>
    </>;
  },
  place_value_chart: ({ number }) => <>
    {['Yüz bin', 'On bin', 'Bin', 'Yüz', 'On', 'Bir'].map((label, index) => {
      const x = index * 96 + 12;
      return <g key={label}>
        <rect x={x} y="85" width="92" height="110" rx="7" fill={index === 0 ? '#dbeafe' : '#f8fafc'} stroke="#94a3b8" />
        <text x={x + 46} y="118" fontSize="14">{label}</text>
        <text x={x + 46} y="169" fontSize="32">{String(number)[index]}</text>
      </g>;
    })}
  </>,
  circle_elements: () => <CircleElements />,
  circle_element_target: ({ element }) => <>
    <CircleElements />
    {element === 'çap' && <><line x1="205" y1="145" x2="395" y2="145" stroke={ORANGE} strokeWidth="6" /><Arrow x1={250} y1={225} x2={250} y2={147} /></>}
    {element === 'yarıçap' && <><line x1="300" y1="145" x2="348" y2="63" stroke={ORANGE} strokeWidth="6" /><Arrow x1={405} y1={88} x2={327} y2={98} /></>}
    {element === 'merkez' && <><circle cx="300" cy="145" r="8" fill={ORANGE} /><Arrow x1={390} y1={195} x2={304} y2={148} /></>}
  </>,
  point_line_distance: () => <>
    <line x1="110" y1="215" x2="490" y2="215" stroke={BLUE} strokeWidth="5" />
    <circle cx="300" cy="70" r="6" fill={INK} /><text x="322" y="66">A</text>
  </>,
  protractor_angle: ({ degrees }) => {
    const cx = 300;
    const cy = 210;
    const radians = (degrees * Math.PI) / 180;
    return <>
      <path d="M155 210 A145 145 0 0 1 445 210" fill="none" stroke={BLUE} strokeWidth="4" />
      <line x1="155" y1="210" x2="445" y2="210" stroke={BLUE} strokeWidth="3" />
      {Array.from({ length: 37 }, (_, index) => {
        const tick = index * 5;
        const angle = (tick * Math.PI) / 180;
        const inner = 145 - (tick % 30 === 0 ? 19 : tick % 10 === 0 ? 11 : 6);
        return <g key={tick}>
          <line x1={cx + inner * Math.cos(angle)} y1={cy - inner * Math.sin(angle)} x2={cx + 145 * Math.cos(angle)} y2={cy - 145 * Math.sin(angle)} stroke={INK} strokeWidth="2" />
          {tick % 30 === 0 && <text x={cx + 108 * Math.cos(angle)} y={tick === 0 || tick === 180 ? 195 : cy - 108 * Math.sin(angle) + 6} fontSize="14">{tick}</text>}
        </g>;
      })}
      <line x1={cx} y1={cy} x2="433" y2={cy} stroke="#ef4444" strokeWidth="5" />
      <line x1={cx} y1={cy} x2={cx + 133 * Math.cos(radians)} y2={cy - 133 * Math.sin(radians)} stroke="#ef4444" strokeWidth="5" />
      <circle cx={cx} cy={cy} r="6" fill={INK} />
    </>;
  },
  rational_compare: ({ a, b }) => {
    const first = rationalValue(a);
    const second = rationalValue(b);
    const spread = Math.max(Math.abs(first - second), 0.5);
    const left = Math.min(first, second) - spread * 0.35;
    const right = Math.max(first, second) + spread * 0.35;
    const position = (value) => 80 + ((value - left) / (right - left)) * 440;
    const firstX = position(first);
    const secondX = position(second);
    return <>
      <line x1="80" y1="145" x2="520" y2="145" stroke={INK} strokeWidth="4" />
      <polygon points="80,145 94,137 94,153" fill={INK} />
      <polygon points="520,145 506,137 506,153" fill={INK} />
      {left <= 0 && 0 <= right && <g><line x1={position(0)} y1="135" x2={position(0)} y2="155" stroke={INK} strokeWidth="2" /><text x={position(0)} y="182" fontSize="18">0</text></g>}
      <line x1={firstX} y1="102" x2={firstX} y2="145" stroke={BLUE} strokeWidth="2" strokeDasharray="5 4" />
      <line x1={secondX} y1="145" x2={secondX} y2="192" stroke={ORANGE} strokeWidth="2" strokeDasharray="5 4" />
      <circle cx={firstX} cy="145" r="8" fill={BLUE} />
      <circle cx={secondX} cy="145" r="6" fill={ORANGE} />
      <text x={firstX} y="88" fill="#1d4ed8" fontSize="27">{a}</text>
      <text x={secondX} y="230" fill="#c2410c" fontSize="27">{b}</text>
    </>;
  },
  shape_sequence: (spec) => spec.spec_version === '2.0' ? <ShapePatternVisual {...spec} /> : <>
    {spec.sequence.split(',').map((value, index) => {
      const symbol = value.trim();
      const centerX = 85 + index * 86;
      return <g key={index}>
        <rect x={centerX - 36} y="94" width="72" height="92" rx="14" fill={symbol === '?' ? '#fff7ed' : '#eff6ff'} stroke={symbol === '?' ? ORANGE : '#bfdbfe'} strokeWidth="2" strokeDasharray={symbol === '?' ? '6 5' : undefined} />
        <ShapeSymbol symbol={symbol} centerX={centerX} centerY={140} />
      </g>;
    })}
  </>,
};

function ShapeSymbol({ symbol, centerX, centerY }) {
  if (symbol === '▲') return <polygon points={`${centerX},${centerY - 27} ${centerX - 28},${centerY + 23} ${centerX + 28},${centerY + 23}`} fill={BLUE} />;
  if (symbol === '■') return <rect x={centerX - 24} y={centerY - 24} width="48" height="48" rx="3" fill={BLUE} />;
  if (symbol === '⬟') {
    const points = Array.from({ length: 5 }, (_, index) => {
      const angle = -Math.PI / 2 + index * 2 * Math.PI / 5;
      return `${centerX + 29 * Math.cos(angle)},${centerY + 29 * Math.sin(angle)}`;
    }).join(' ');
    return <polygon points={points} fill={BLUE} />;
  }
  return <text x={centerX} y={centerY + 14} fontSize="48" fill={INK}>?</text>;
}

function CircleElements() {
  return <>
    <circle cx="300" cy="145" r="95" fill="#eff6ff" stroke={BLUE} strokeWidth="4" />
    <line x1="205" y1="145" x2="395" y2="145" stroke={BLUE} strokeWidth="3" />
    <line x1="300" y1="145" x2="348" y2="63" stroke="#ef4444" strokeWidth="3" />
    <circle cx="300" cy="145" r="5" fill={INK} /><text x="300" y="170" fontSize="17">O</text>
  </>;
}

function Arrow({ x1, y1, x2, y2 }) {
  const angle = Math.atan2(y2 - y1, x2 - x1);
  const wing = 13;
  const spread = 0.5;
  const points = [
    [x2, y2],
    [x2 - wing * Math.cos(angle - spread), y2 - wing * Math.sin(angle - spread)],
    [x2 - wing * Math.cos(angle + spread), y2 - wing * Math.sin(angle + spread)],
  ].map((point) => point.join(',')).join(' ');
  return <><line {...{ x1, y1, x2, y2 }} stroke={ORANGE} strokeWidth="5" /><polygon points={points} fill={ORANGE} /></>;
}

export function supportsMathVisual(spec) {
  if (!spec || typeof spec !== 'object' || !Object.prototype.hasOwnProperty.call(renderers, spec.renderer)) return false;
  if (primitiveValidators[spec.renderer]) return primitiveValidators[spec.renderer](spec);
  if (['neighbor_tens', 'minimal_number_position'].includes(spec.renderer)) {
    const unit = spec.unit ?? 10;
    return [10, 100].includes(unit) && Number.isInteger(spec.number) && Number.isInteger(spec.lower) && spec.lower >= 0 && spec.lower % unit === 0 && spec.upper === spec.lower + unit && spec.number >= spec.lower && spec.number < spec.upper;
  }
  if (spec.renderer === 'analog_clock') return Number.isInteger(spec.hour) && spec.hour >= 1 && spec.hour <= 12 && Number.isInteger(spec.minute) && spec.minute >= 0 && spec.minute < 60;
  if (spec.renderer === 'numberline_midpoint') return Number.isInteger(spec.lower) && Number.isInteger(spec.upper) && spec.lower >= 0 && spec.lower < spec.upper && spec.upper <= 1000000;
  if (spec.renderer === 'numberline_interval') {
    const lower = rationalValue(spec.lower), upper = rationalValue(spec.upper);
    return lower !== null && upper !== null && lower >= -1000000 && lower < upper && upper <= 1000000;
  }
  if (spec.renderer === 'numberline_pattern') {
    const { values, direction } = spec;
    if (!Array.isArray(values) || values.length !== 5 || values.filter((v) => v === null).length !== 1
      || !['ascending', 'descending'].includes(direction) || !values.every((v) => v === null || (Number.isInteger(v) && v >= 0 && v <= 1000000))) return false;
    const visible = values.map((v, i) => [i, v]).filter(([, v]) => v !== null);
    const [[i, a], [j, b]] = visible;
    const step = (b - a) / (j - i), start = a - i * step;
    return Number.isInteger(step) && step > 0 && start >= 0 && start + 4 * step <= 1000000 && visible.every(([k, v]) => v === start + k * step);
  }
  if (spec.renderer === 'base10_blocks') return Number.isInteger(spec.number) && spec.number >= 0 && spec.number <= 99;
  if (spec.renderer === 'place_value_chart') return Number.isInteger(spec.number) && spec.number >= 100000 && spec.number <= 999999;
  if (spec.renderer === 'numberline_rounding') return Number.isInteger(spec.number) && Number.isInteger(spec.unit ?? 10) && (spec.unit ?? 10) > 0;
  if (spec.renderer === 'protractor_angle') return Number.isFinite(spec.degrees) && spec.degrees > 0 && spec.degrees < 180;
  if (spec.renderer === 'circle_element_target') return ['çap', 'yarıçap', 'merkez'].includes(spec.element);
  if (spec.renderer === 'rational_compare') return rationalValue(spec.a) !== null && rationalValue(spec.b) !== null;
  if (spec.renderer === 'shape_sequence') {
    return supportsShapePattern(spec);
  }
  return true;
}

export default function MathVisual({ spec }) {
  if (!supportsMathVisual(spec)) return null;
  const Draw = renderers[spec.renderer];
  const description = spec.accessibility?.alt_text || (spec.renderer === 'shape_sequence' && typeof spec.sequence === 'string'
    ? `Şekil örüntüsü: ${spec.sequence.split(',').map((value) => shapeNames[value.trim()]).join(', ')}`
    : 'Matematik sorusu çizimi');
  const width = spec.renderer === 'analog_clock' ? 280 : 600;
  return <svg data-testid="math-visual" role="img" aria-label={description} width="100%" viewBox={`0 0 ${width} 280`} preserveAspectRatio="xMidYMid meet" style={{ display: 'block', maxHeight: '100%' }}>
    <rect width={width} height="280" rx="18" fill="white" />
    <g fill={INK} fontFamily="Arial, sans-serif" fontWeight="700" fontSize="22" textAnchor="middle">
      <Draw {...spec} />
    </g>
  </svg>;
}
